import { Button, tokens } from "@blurb/codex-react";
import { createStyles, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { useShowSuccessToast } from "@rpi/openapi-core";
import { Outlet, useOutletContext, useParams } from "react-router-dom";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { shouldRetry } from "../../utils/query.util";
import {
  getProductSaveErrorMessage,
  NameAndSkuEditor,
  PRODUCT_NAME_REQUIRED_ERROR,
  PRODUCT_SKU_REQUIRED_ERROR,
} from "./NameAndSkuEditor";

const EDIT_FORM_ID = "editProductForm";

const useStyles = createStyles({
  wrapper: {
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    padding: tokens.spacing[6],
    gap: tokens.spacing[2],
  },
});

export const CustomerProductEditViewV2 = ({ isOpened, onClose }: { isOpened: boolean; onClose: () => void }) => {
  const { classes } = useStyles();
  const { id: stringId } = useParams();
  const id = stringId ? Number.parseInt(stringId) : undefined;

  const { data, isLoading, error } = GraphQL.useCustomerProductQuery<GraphQL.CustomerProductQuery, unknown>(
    { id: id ?? -1 },
    {
      enabled: !!id && isOpened,
      refetchOnMount: true,
      retry: shouldRetry(onClose),
    }
  );
  const customerProduct = data?.customerProduct;
  const openapiProduct = customerProduct?.openapiProduct;

  const outletContext: EditProductV2Props | undefined =
    isOpened && customerProduct && openapiProduct ? { customerProduct, openapiProduct, onClose } : undefined;

  return (
    <RpaDrawer title="Edit Product" isLoading={isLoading} error={error} opened={isOpened} onClose={onClose}>
      <div className={classes.wrapper}>{outletContext && <Outlet context={outletContext} />}</div>
    </RpaDrawer>
  );
};

type CustomerProductData = GraphQLQueryData<GraphQL.CustomerProductQuery>;

interface EditProductV2Props {
  customerProduct: CustomerProductData;
  openapiProduct: NonNullable<CustomerProductData["openapiProduct"]>;
  onClose: () => void;
}

export const EditProductV2 = () => {
  const { classes } = useStyles();
  const { customerProduct, openapiProduct, onClose } = useOutletContext<EditProductV2Props>();
  const showSuccessToast = useShowSuccessToast();

  const form = useForm({
    initialValues: {
      name: customerProduct.name ?? "",
      newCustomerSku: customerProduct.customerSku,
    },
    validate: {
      name: (value) => (value ? null : PRODUCT_NAME_REQUIRED_ERROR),
      newCustomerSku: (value) => (value ? null : PRODUCT_SKU_REQUIRED_ERROR),
    },
  });

  const { mutate: tryEdit, isLoading: loading } = GraphQL.useEditCustomerProductMutation({
    onSuccess() {
      showSuccessToast("Your product was updated successfully!");
      onClose();
    },
  });

  const handleSave = (values: typeof form.values) => {
    tryEdit(
      { id: customerProduct.id, input: values },
      {
        onError(error) {
          const message = getProductSaveErrorMessage(error);
          if (message) {
            form.setFieldError("newCustomerSku", message);
          }
        },
      }
    );
  };

  const nameError = typeof form.errors.name === "string" ? form.errors.name : undefined;
  const skuError = typeof form.errors.newCustomerSku === "string" ? form.errors.newCustomerSku : undefined;

  return (
    <>
      <form id={EDIT_FORM_ID} onSubmit={form.onSubmit(handleSave)}>
        <NameAndSkuEditor
          openapiProduct={openapiProduct}
          productName={form.values.name}
          productNameError={nameError}
          onProductNameChanged={(value) => form.setFieldValue("name", value)}
          customerSku={form.values.newCustomerSku}
          customerSkuError={skuError}
          onCustomerSkuChanged={(value) => form.setFieldValue("newCustomerSku", value)}
          loading={loading}
        />
      </form>
      <RpaDrawer.Footer>
        <Stack className={classes.footer}>
          <Button
            type="submit"
            form={EDIT_FORM_ID}
            variant="filled"
            intent="primary"
            fullWidth
            loading={loading}
            data-testid="saveProductChangesButton"
          >
            Save Changes
          </Button>
          <Button type="button" variant="text" intent="primary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </Stack>
      </RpaDrawer.Footer>
    </>
  );
};
